import { useEffect, useState } from "react"

import { ABIParameter } from '@remixproject/plugin-api'

import Button from 'react-bootstrap/Button'
import CloseButton from 'react-bootstrap/CloseButton'
import Modal from 'react-bootstrap/Modal'

import { decryptString, decryptUint } from "@coti-io/coti-sdk-typescript"
import { ENCRYPTED_OUTPUT_TYPES } from "../../utils/constants"
import { useEnvironmentStore } from "../../zustand/environmentStore"

type ReturnDataItemProps = {
    index: number,
    returnValue: ReturnValue
}

type ReturnDataProps = {
    res: any,
    functionOutputs: ABIParameter[]
}

type ReturnValue = {
    name: string,
    type: "primitive" | "array" | "tuple",
    baseType?: string,
    value?: string
    encryptedValue?: string,
    components?: { [index: number]: ReturnValue }
}

const ReturnDataItem = ({ index, returnValue }: ReturnDataItemProps) => {
    const [showModal, setShowModal] = useState(false)
    const [isEncryptedValue, setIsEncryptedValue] = useState(false)
    const [type, setType] = useState('')
    const [value, setValue] = useState('')
    const [encryptedValue, setEncryptedValue] = useState('')

    const formatReturnValue = (rv: ReturnValue) => {
        if (rv.components) {
            const componentTypes: string[] = []
            const componentValues: string[] = []
            const componentEncryptedValues: string[] = []
            
            Object.values(rv.components).forEach((component, idx) => {
                const [t, v, e] = formatReturnValue(component)
                componentTypes.push(t!)
                componentValues.push(v!)
                componentEncryptedValues.push(e!)
            })

            if (rv.type === 'array') {
                if (rv.baseType === 'tuple' || rv.baseType === 'ctString')
                    return [`${componentTypes[0].toString()}[]`, `[${componentValues.toString()}]`, `[${componentEncryptedValues.toString()}]`]
                else {
                    return [`${rv.baseType}[]`, `[${componentValues.toString()}]`, `[${componentEncryptedValues.toString()}]`]
                }
            } else {
                return [`tuple(${componentTypes.toString()})` ,`(${componentValues.toString()})`, `(${componentEncryptedValues.toString()})`]
            }
        } else {
            let e = rv.value

            if (rv.encryptedValue !== undefined) {
                setIsEncryptedValue(true)
                e = rv.encryptedValue
            }

            return [rv.baseType, rv.value!.toString(), e?.toString()]
        }
    }

    useEffect(() => {
        const [t, v, e] = formatReturnValue(returnValue)

        setType(t!)
        setValue(v!)
        setEncryptedValue(e!)
    }, [returnValue])

    return (
        <>
            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Modal.Header>
                    <CloseButton onClick={() => setShowModal(false)} />
                </Modal.Header>

                <Modal.Body className='encrypted-input-modal'>
                    <p>Decrypted value: {value}</p>
                    <p>Encrypted value: {encryptedValue?.toString()}</p>
                </Modal.Body>
            </Modal>

            <p key={index}><b>{index}:</b> {type}: {returnValue.name} {value}</p>

            {isEncryptedValue &&
                <Button size='sm' variant='outline-primary' className='encrypted-input-modal-btn' onClick={() => setShowModal(true)}><b>i</b></Button>
            }
        </>
    )
}

const ReturnData = ({ res, functionOutputs }: ReturnDataProps) => {
    const [returnData, setReturnData] = useState<{ [index: number]: ReturnValue }>({})
    const { userKey } = useEnvironmentStore()

    useEffect(() => {
        if (!res) {
            return
        }

        res = Array.isArray(res) ? res : [res]
        const formattedData = formatReturnData(res, functionOutputs)

        setReturnData(formattedData)
    }, [res])

    const formatReturnData = (res: any, abi: ABIParameter[]) => {
        const formatReturnDataHelper = (result: any, output: ABIParameter): ReturnValue => {
            const formatArray = (arrayResult: any, arrayOutput: ABIParameter) => {
                const data: ReturnValue = {
                    name: arrayOutput.name,
                    type: "array",
                    baseType: arrayOutput.type.slice(0, arrayOutput.type.length - 2),
                    components: {}
                }

                if (arrayOutput.components) {
                    if (arrayOutput.type.slice(0, arrayOutput.type.length - 2).includes('[]')) {
                        const componentOutput = { ...arrayOutput }

                        componentOutput.type = componentOutput.type.slice(0, componentOutput.type.length - 2)

                        if ('internalType' in componentOutput) {
                            componentOutput.internalType = (componentOutput.internalType as string).slice(0, (componentOutput.internalType as string).length - 2)
                        }
    
                        Object.values(arrayResult).forEach((nestedArr: any, idx: number) => {
                            data.components![idx] = formatReturnDataHelper(nestedArr, componentOutput)
                        })
                    } else {
                        const internalType = 'internalType' in arrayOutput ? arrayOutput.internalType as string : arrayOutput.type
                        const baseType = internalType.slice(0, internalType.length - 2).split(' ')[1]

                        if (baseType === 'ctString') {
                            data.baseType = baseType

                            arrayResult.forEach((value: any, idx1: number) => {
                                const tuple: ReturnValue = {
                                    name: "",
                                    type: "tuple",
                                    baseType: baseType,
                                    value: decryptString(value, userKey),
                                    encryptedValue: value
                                }
        
                                data.components![idx1] = tuple
                            })
                        } else {
                            arrayResult.forEach((value: any, idx1: number) => {
                                const tuple: ReturnValue = {
                                    name: arrayOutput.name,
                                    type: "tuple",
                                    components: {}
                                }
        
                                arrayOutput.components!.forEach((component, idx2) => {
                                    tuple.components![idx2] = formatReturnDataHelper(value[idx2], component)
                                })
        
                                data.components![idx1] = tuple
                            })
                        }
                    }
                } else {
                    if (arrayOutput.type.slice(0, arrayOutput.type.length - 2).includes('[]')) {
                        const componentOutput: ABIParameter = {
                            name: arrayOutput.name,
                            type: arrayOutput.type.slice(0, arrayOutput.type.length - 2)
                        }
    
                        Object.values(arrayResult).forEach((nestedArr: any, idx: number) => {
                            data.components![idx] = formatReturnDataHelper(nestedArr, componentOutput)
                        })
                    } else {
                        const internalType = 'internalType' in arrayOutput ? arrayOutput.internalType as string : arrayOutput.type
                        const baseType = internalType.slice(0, internalType.length - 2)

                        data.baseType = baseType
                        
                        if (ENCRYPTED_OUTPUT_TYPES.includes(baseType)) {
                            arrayResult.forEach((value: any, index: number) => {
                                let decryptedValue = value !== BigInt(0) ? decryptUint(value, userKey) : value
                                
                                if (baseType === 'ctBool') {
                                    decryptedValue = Boolean(decryptedValue)
                                }

                                data.components![index] = {
                                    name: arrayOutput.name,
                                    type: "primitive",
                                    baseType: baseType,
                                    value: decryptedValue,
                                    encryptedValue: value
                                }
                            })
                        } else {
                            arrayResult.forEach((value: any, index: number) => {
                                data.components![index] = {
                                    name: arrayOutput.name,
                                    type: "primitive",
                                    baseType: baseType,
                                    value: value
                                }
                            })
                        }
                    }
                }

                return data
            }

            const formatTuple = (tupleResult: any, tupleOutput: ABIParameter) => {
                const data: ReturnValue = {
                    name: tupleOutput.name,
                    type: 'tuple',
                }

                let internalType: string = tupleOutput.type

                if ('internalType' in tupleOutput) {
                    internalType = tupleOutput.internalType as string
                }

                if (internalType.split(' ')[1] === 'ctString') {
                    data.baseType = 'ctString'
                    data.value = decryptString(tupleResult, userKey)
                    data.encryptedValue = tupleResult
                } else {
                    data.components = {}

                    tupleOutput.components!.forEach((component, index) => {
                        data.components![index] = formatReturnDataHelper(tupleResult[index], component)
                    })
                }

                return data
            }

            const formatPrimitive = (primitiveResult: any, primitiveOutput: ABIParameter): ReturnValue => {
                const baseType = 'internalType' in primitiveOutput ? primitiveOutput.internalType as string : primitiveOutput.type

                let value = undefined
                let encryptedValue = undefined
                
                if (ENCRYPTED_OUTPUT_TYPES.includes(baseType)) {
                    value = primitiveResult !== BigInt(0) ? decryptUint(result, userKey) : primitiveResult

                    if (baseType == 'ctBool') {
                        value = Boolean(value)
                    }

                    encryptedValue = primitiveResult
                } else {
                    value = primitiveResult
                }

                return {
                    name: primitiveOutput.name,
                    type: "primitive",
                    baseType: baseType,
                    value: value,
                    encryptedValue: encryptedValue
                }
            }

            if (output.type.includes('[]')) {
                return formatArray(result, output)
            }
            
            if (output.components) {
                return formatTuple(result, output)
            }

            return formatPrimitive(result, output)
        }

        res = typeof res === 'object' ? res : { 0: res }

        const data: { [index: number]: ReturnValue } = {}

        abi.forEach((output, index) => {
            data[index] = formatReturnDataHelper(res[index], output)
        })

        return data
    }

    return (
        <div className='return-data-container'>
            {Object.entries(returnData).map(([key, value], dataIndex) => {
                return (
                    <ReturnDataItem index={dataIndex} returnValue={value} />
                )
            })}
        </div>
    )
}

export default ReturnData